/**
 * Multi-resolution peak pyramid for waveform display (Author + dock thumbnail).
 * Buckets store min/max of mono PCM so canvas draws cheaply at any zoom.
 */

export type PeakLevel = {
  /** Samples (frames) per bucket at this level. */
  samplesPerBucket: number;
  min: Float32Array;
  max: Float32Array;
};

export type PeakPyramid = {
  lengthFrames: number;
  levels: PeakLevel[];
};

const MIN_BUCKETS = 32;

/** Build a pyramid: level 0 ≈ 1 sample/bucket (or coarsened for long files). */
export function buildPeakPyramid(pcm: Float32Array, maxLevels = 10): PeakPyramid {
  const lengthFrames = pcm.length;
  if (lengthFrames === 0) {
    return { lengthFrames: 0, levels: [] };
  }

  // Start with a dense level (~1–4 frames per bucket) so zoom stays crisp.
  const baseSpb = lengthFrames > 2_000_000 ? 8 : lengthFrames > 500_000 ? 4 : lengthFrames > 100_000 ? 2 : 1;
  const levels: PeakLevel[] = [];
  let spb = baseSpb;
  let min = new Float32Array(Math.ceil(lengthFrames / spb));
  let max = new Float32Array(min.length);
  fillPeaksFromPcm(pcm, spb, min, max);
  levels.push({ samplesPerBucket: spb, min, max });

  while (levels.length < maxLevels && min.length > MIN_BUCKETS) {
    spb *= 2;
    const nextMin = new Float32Array(Math.ceil(lengthFrames / spb));
    const nextMax = new Float32Array(nextMin.length);
    downsamplePeaks(min, max, nextMin, nextMax);
    levels.push({ samplesPerBucket: spb, min: nextMin, max: nextMax });
    min = nextMin;
    max = nextMax;
  }

  return { lengthFrames, levels };
}

function fillPeaksFromPcm(
  pcm: Float32Array,
  samplesPerBucket: number,
  min: Float32Array,
  max: Float32Array,
): void {
  for (let b = 0; b < min.length; b++) {
    const start = b * samplesPerBucket;
    const end = Math.min(pcm.length, start + samplesPerBucket);
    let lo = 1;
    let hi = -1;
    if (start >= end) {
      min[b] = 0;
      max[b] = 0;
      continue;
    }
    for (let i = start; i < end; i++) {
      const v = pcm[i]!;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    min[b] = lo;
    max[b] = hi;
  }
}

function downsamplePeaks(
  srcMin: Float32Array,
  srcMax: Float32Array,
  dstMin: Float32Array,
  dstMax: Float32Array,
): void {
  for (let b = 0; b < dstMin.length; b++) {
    const i0 = b * 2;
    const i1 = Math.min(srcMin.length - 1, i0 + 1);
    dstMin[b] = Math.min(srcMin[i0]!, srcMin[i1]!);
    dstMax[b] = Math.max(srcMax[i0]!, srcMax[i1]!);
  }
}

/** Choose the densest level that still has ≤ ~2 samples per pixel. */
export function pickPeakLevel(
  pyramid: PeakPyramid,
  viewStart: number,
  viewEnd: number,
  widthPx: number,
): PeakLevel | null {
  if (pyramid.levels.length === 0 || widthPx <= 0) {
    return null;
  }
  const viewFrames = Math.max(1, viewEnd - viewStart);
  const targetSpb = viewFrames / Math.max(1, widthPx);
  let best = pyramid.levels[pyramid.levels.length - 1]!;
  for (const level of pyramid.levels) {
    if (level.samplesPerBucket <= targetSpb * 2) {
      best = level;
    } else {
      break;
    }
  }
  // Prefer denser when possible
  for (let i = pyramid.levels.length - 1; i >= 0; i--) {
    const level = pyramid.levels[i]!;
    if (level.samplesPerBucket <= Math.max(1, targetSpb)) {
      return level;
    }
    best = level;
  }
  return best;
}

export type ColumnPeak = { min: number; max: number };

/** Resample a pyramid level into one min/max per canvas column. */
export function columnsFromPeaks(
  level: PeakLevel,
  viewStart: number,
  viewEnd: number,
  widthPx: number,
): ColumnPeak[] {
  const cols: ColumnPeak[] = new Array(widthPx);
  const viewFrames = Math.max(1, viewEnd - viewStart);
  for (let x = 0; x < widthPx; x++) {
    const f0 = viewStart + (x / widthPx) * viewFrames;
    const f1 = viewStart + ((x + 1) / widthPx) * viewFrames;
    const b0 = Math.max(0, Math.floor(f0 / level.samplesPerBucket));
    const b1 = Math.min(level.min.length - 1, Math.floor((f1 - 1e-9) / level.samplesPerBucket));
    let lo = 1;
    let hi = -1;
    if (b0 > b1 || level.min.length === 0) {
      cols[x] = { min: 0, max: 0 };
      continue;
    }
    for (let b = b0; b <= b1; b++) {
      const mn = level.min[b]!;
      const mx = level.max[b]!;
      if (mn < lo) lo = mn;
      if (mx > hi) hi = mx;
    }
    cols[x] = { min: lo, max: hi };
  }
  return cols;
}

/** Clamp a frame index into [0, length]. */
export function clampFrame(frame: number, length: number): number {
  if (!Number.isFinite(frame) || length <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(length, Math.round(frame)));
}

/** Normalize region so start ≤ end and both are in range. */
export function normalizeRegion(
  start: number,
  end: number,
  length: number,
): { start: number; end: number } {
  if (length <= 0) {
    return { start: 0, end: 0 };
  }
  let a = clampFrame(start, length);
  let b = clampFrame(end, length);
  if (b < a) {
    const t = a;
    a = b;
    b = t;
  }
  if (b === a) {
    b = Math.min(length, a + 1);
  }
  return { start: a, end: b };
}

/** Normalize loop braces inside a region. */
export function normalizeLoopRange(
  start: number,
  end: number,
  regionStart: number,
  regionEnd: number,
): { start: number; end: number } {
  const lo = Math.min(regionStart, regionEnd);
  const hi = Math.max(regionStart, regionEnd);
  if (hi <= lo + 1) {
    return { start: lo, end: Math.max(lo + 1, hi) };
  }
  let a = Math.round(start);
  let b = Math.round(end);
  if (b < a) {
    const t = a;
    a = b;
    b = t;
  }
  a = Math.max(lo, Math.min(hi - 1, a));
  b = Math.max(a + 1, Math.min(hi, b));
  return { start: a, end: b };
}

/** Build an enabled loop spanning the current selection/region. */
export function loopFromSelection(
  region: { start: number; end: number },
  sampleRate: number,
  previous?: { xfade?: number; mode?: "continuous" | "untilRelease" },
): {
  enabled: true;
  start: number;
  end: number;
  xfade: number;
  mode: "untilRelease";
} {
  const start = Math.round(region.start);
  const end = Math.max(start + 1, Math.round(region.end));
  const len = end - start;
  const twentyMs = Math.max(16, Math.round(sampleRate * 0.02));
  const fallback = Math.max(0, Math.min(twentyMs, Math.floor(len / 4)));
  const max = Math.max(0, Math.floor(len / 2) - 1);
  const xfade =
    previous?.xfade != null && previous.xfade > 0
      ? Math.min(Math.round(previous.xfade), max)
      : Math.min(fallback, max);
  // Selection-braced loops are usually chops with attack/tail — exit on release.
  return { enabled: true, start, end, xfade, mode: "untilRelease" };
}

/** Map client X within an element to a frame under the current view. */
export function clientXToFrame(
  clientX: number,
  element: HTMLElement,
  viewStart: number,
  viewEnd: number,
): number {
  const rect = element.getBoundingClientRect();
  const t = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
  return viewStart + Math.max(0, Math.min(1, t)) * (viewEnd - viewStart);
}
